import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { COMPANY, NAV_LINKS } from "@/lib/data";
import { useBooking } from "@/context/BookingContext";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-9 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#050505]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-center justify-between h-20">
        <a href="#top" data-testid="nav-logo" onClick={(e) => { e.preventDefault(); handleNav("#top"); }} className="flex items-center gap-3">
          <img src={COMPANY.logo} alt="Auto-Hire STL" className="h-12 w-12 object-contain rounded-md" />
          <span className="font-display font-extrabold text-lg tracking-tight hidden sm:block">
            AutoHire <span className="text-gold-gradient">STL</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => handleNav(l.href)}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-200 tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={COMPANY.phoneHref}
            data-testid="nav-phone"
            className="hidden md:flex items-center gap-2 text-sm text-white/80 hover:text-[#F2B705] transition-colors"
          >
            <Phone size={15} className="text-[#F2B705]" />
            <span className="font-medium">{COMPANY.phone}</span>
          </a>
          <button
            data-testid="nav-book-now"
            onClick={() => openBooking("")}
            className="bg-[#F2B705] text-black font-semibold text-sm px-5 py-2.5 rounded-md hover:bg-white transition-colors duration-300"
          >
            Request a Reservation
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden text-white p-1"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-[#0A0A0A] border-t border-white/10"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => handleNav(l.href)}
                  className="text-left py-3 text-white/80 hover:text-[#F2B705] border-b border-white/5 font-medium"
                >
                  {l.label}
                </button>
              ))}
              <a href={COMPANY.phoneHref} className="flex items-center gap-2 py-3 text-[#F2B705] font-medium">
                <Phone size={16} /> {COMPANY.phone}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
